// src/App.js
import { useState } from 'react';
import { ShoppingCart, LogOut, Package, LayoutDashboard, FileText, User, UserPlus, Building2 } from 'lucide-react';

// Auth
import { AuthProvider, useAuth } from './hooks/useAuth';
import { Login } from './components/auth/Login';
import { Register } from './components/auth/Register';
import { EmailVerificationRequired, ApprovalPending } from './components/auth/PendingStates';
import { AuthAction } from './components/auth/AuthAction';

// Hooks
import { useProducts } from './hooks/useProducts';
import { useOrders, useAllOrders, useRepOrders } from './hooks/useOrders';
import { useCart } from './hooks/useCart';
import { useAllProducts } from './hooks/useProducts';
import { useReps } from './hooks/useReps';
import { useCustomers, useRepCustomers } from './hooks/useCustomers';
import { useCustomerData, useCustomerOrders } from './hooks/useCustomerPortal';

// Wholesaler/Customer Components (legacy portal for reps who login)
import { ProductCatalog } from './components/wholesaler/ProductCatalog';
import { Cart } from './components/wholesaler/Cart';
import { Checkout } from './components/wholesaler/Checkout';
import { OrderHistory } from './components/wholesaler/OrderHistory';
import { Profile } from './components/wholesaler/Profile';

// Admin Components
import { AdminDashboard } from './components/admin/AdminDashboard';
import { OrderManagement } from './components/admin/OrderManagement';
import { ProductManagement } from './components/admin/ProductManagement';
import { RepManagement } from './components/admin/RepManagement';
import { CustomerManagement } from './components/admin/CustomerManagement';
import { CommissionManagement } from './components/admin/CommissionManagement';
import { InvoiceManagement } from './components/admin/InvoiceManagement';

// Rep Components
import { RepDashboard } from './components/rep/RepDashboard';
import { RepPlaceOrder } from './components/rep/RepPlaceOrder';
import { RepOrders } from './components/rep/RepOrders';

// Customer Components
import { CustomerDashboard } from './components/customer/CustomerDashboard';
import { CustomerShop } from './components/customer/CustomerShop';
import { CustomerOrders } from './components/customer/CustomerOrders';
import { CustomerProfile } from './components/customer/CustomerProfile';

function AppContent() {
  const { user, userData, loading, signOut, isAdmin, isRep, isWholesaler, isApproved, isEmailVerified } = useAuth();
  const [authView, setAuthView] = useState('login');
  const [view, setView] = useState('dashboard');

  // Wholesaler/Customer hooks
  const { products, loading: productsLoading } = useProducts(
    userData?.pricingTier || 'standard',
    userData?.tierDiscount || 0
  );
  const { orders, loading: ordersLoading } = useOrders(user?.uid);
  const { cart, addToCart, updateQuantity, removeFromCart, clearCart, subtotal, ccFee, totalWithFee, itemCount } = useCart();

  // Admin hooks
  const { orders: allOrders, loading: allOrdersLoading } = useAllOrders();
  const { products: allProducts, loading: allProductsLoading } = useAllProducts();
  const { reps, loading: repsLoading } = useReps();
  const { customers: allCustomers, loading: customersLoading } = useCustomers();

  // Rep hooks
  const { customers: repCustomers, loading: repCustomersLoading } = useRepCustomers(isRep ? user?.uid : null);
  const { orders: repOrders, loading: repOrdersLoading } = useRepOrders(isRep ? user?.uid : null);

  // Customer portal hooks
  const { customer: customerData, loading: customerDataLoading } = useCustomerData(userData?.customerId);
  const { orders: customerOrders, loading: customerOrdersLoading } = useCustomerOrders(userData?.customerId);

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-4 border-gray-300 border-t-black rounded-full mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Not logged in
  if (!user) {
    // Check if this is an auth action (email verify, password reset)
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('mode') && urlParams.get('oobCode')) {
      return (
        <AuthAction 
          onComplete={() => {
            window.history.replaceState({}, '', '/');
            setAuthView('login');
          }} 
        />
      );
    }

    if (authView === 'register') {
      return <Register onSwitchToLogin={() => setAuthView('login')} />;
    }
    return <Login onSwitchToRegister={() => setAuthView('register')} />;
  }

  // Email not verified
  if (!isEmailVerified) {
    return (
      <div className="min-h-screen bg-gray-50">
        <nav className="bg-black text-white p-4">
          <div className="max-w-7xl mx-auto flex justify-between items-center">
            <h1 className="text-xl font-bold">4Skin Wholesale</h1>
            <button onClick={signOut} className="flex items-center gap-2 hover:underline">
              <LogOut size={16} /> Logout
            </button>
          </div>
        </nav>
        <EmailVerificationRequired userEmail={user.email} />
      </div>
    );
  }

  // Wholesaler not approved
  if (isWholesaler && !isApproved) {
    return (
      <div className="min-h-screen bg-gray-50">
        <nav className="bg-black text-white p-4">
          <div className="max-w-7xl mx-auto flex justify-between items-center">
            <h1 className="text-xl font-bold">4Skin Wholesale</h1>
            <button onClick={signOut} className="flex items-center gap-2 hover:underline">
              <LogOut size={16} /> Logout
            </button>
          </div>
        </nav>
        <ApprovalPending />
      </div>
    );
  }

  // Handle checkout success
  const handleCheckoutSuccess = (orderId) => {
    clearCart();
    setView('orders');
    alert('Order placed successfully!');
  };

  // Admin View
  if (isAdmin) {
    return (
      <div className="min-h-screen bg-gray-50">
        <nav className="bg-black text-white p-4">
          <div className="max-w-7xl mx-auto flex justify-between items-center">
            <h1 className="text-xl font-bold">4Skin Wholesale Admin</h1>
            <div className="flex items-center gap-3 text-sm">
              <button onClick={() => setView('dashboard')} className={`flex items-center gap-1 ${view === 'dashboard' ? 'underline' : 'hover:underline'}`}>
                <LayoutDashboard size={16} /> Dashboard
              </button>
              <button onClick={() => setView('orders')} className={`flex items-center gap-1 ${view === 'orders' ? 'underline' : 'hover:underline'}`}>
                <Package size={16} /> Orders
              </button>
              <button onClick={() => setView('place-order')} className={`flex items-center gap-1 ${view === 'place-order' ? 'underline' : 'hover:underline'}`}>
                <ShoppingCart size={16} /> Place Order
              </button>
              <button onClick={() => setView('reps')} className={`flex items-center gap-1 ${view === 'reps' ? 'underline' : 'hover:underline'}`}>
                <UserPlus size={16} /> Reps
              </button>
              <button onClick={() => setView('customers')} className={`flex items-center gap-1 ${view === 'customers' ? 'underline' : 'hover:underline'}`}>
                <Building2 size={16} /> Customers
              </button>
              <button onClick={() => setView('commissions')} className={`flex items-center gap-1 ${view === 'commissions' ? 'underline' : 'hover:underline'}`}>
                Commissions
              </button>
              <button onClick={() => setView('invoices')} className={`flex items-center gap-1 ${view === 'invoices' ? 'underline' : 'hover:underline'}`}>
                Invoices
              </button>
              <button onClick={() => setView('products')} className={`flex items-center gap-1 ${view === 'products' ? 'underline' : 'hover:underline'}`}>
                <FileText size={16} /> Products
              </button>
              <button onClick={signOut} className="flex items-center gap-2 hover:underline ml-4">
                <LogOut size={16} /> Logout
              </button>
            </div>
          </div>
        </nav>

        <div className="max-w-7xl mx-auto p-6">
          {view === 'dashboard' && <AdminDashboard orders={allOrders} reps={reps} customers={allCustomers} onNavigate={setView} />}
          {view === 'orders' && <OrderManagement orders={allOrders} loading={allOrdersLoading} />}
          {view === 'place-order' && <RepPlaceOrder customers={allCustomers} products={allProducts} onOrderPlaced={() => setView('orders')} />}
          {view === 'reps' && <RepManagement reps={reps} loading={repsLoading} />}
          {view === 'customers' && <CustomerManagement customers={allCustomers} reps={reps} loading={customersLoading} currentUserRole="admin" currentUserId={user?.uid} />}
          {view === 'commissions' && <CommissionManagement reps={reps} orders={allOrders} loading={repsLoading || allOrdersLoading} />}
          {view === 'invoices' && <InvoiceManagement orders={allOrders} customers={allCustomers} reps={reps} products={allProducts} loading={allOrdersLoading || customersLoading} />}
          {view === 'products' && <ProductManagement products={allProducts} loading={allProductsLoading} />}
        </div>
      </div>
    );
  }

  // Rep View
  if (isRep) {
    return (
      <div className="min-h-screen bg-gray-50">
        <nav className="bg-black text-white p-4">
          <div className="max-w-7xl mx-auto flex justify-between items-center">
            <h1 className="text-xl font-bold">4Skin Wholesale - Rep Portal</h1>
            <div className="flex items-center gap-4">
              <button onClick={() => setView('dashboard')} className={`flex items-center gap-1 ${view === 'dashboard' ? 'underline' : 'hover:underline'}`}>
                <LayoutDashboard size={16} /> Dashboard
              </button>
              <button onClick={() => setView('customers')} className={`flex items-center gap-1 ${view === 'customers' ? 'underline' : 'hover:underline'}`}>
                <Building2 size={16} /> My Customers
              </button>
              <button onClick={() => setView('place-order')} className={`flex items-center gap-1 ${view === 'place-order' ? 'underline' : 'hover:underline'}`}>
                <ShoppingCart size={16} /> Place Order
              </button>
              <button onClick={() => setView('orders')} className={`flex items-center gap-1 ${view === 'orders' ? 'underline' : 'hover:underline'}`}>
                <Package size={16} /> Orders
              </button>
              <button onClick={signOut} className="flex items-center gap-2 hover:underline ml-4">
                <LogOut size={16} /> Logout
              </button>
            </div>
          </div>
        </nav>

        <div className="max-w-7xl mx-auto p-6">
          {view === 'dashboard' && <RepDashboard customers={repCustomers} orders={repOrders} userData={userData} />}
          {view === 'customers' && <CustomerManagement customers={repCustomers} reps={[]} loading={repCustomersLoading} currentUserRole="rep" currentUserId={user?.uid} />}
          {view === 'place-order' && <RepPlaceOrder customers={repCustomers} products={allProducts} onOrderPlaced={() => setView('orders')} />}
          {view === 'orders' && <RepOrders orders={repOrders} customers={repCustomers} loading={repOrdersLoading} />}
        </div>
      </div>
    );
  }

  // Customer Portal View (for customers with portal access)
  if (userData?.role === 'customer' && userData?.customerId && customerData) {
    return (
      <div className="min-h-screen bg-gray-50">
        <nav className="bg-black text-white p-4">
          <div className="max-w-7xl mx-auto flex justify-between items-center">
            <h1 className="text-xl font-bold">4Skin Wholesale</h1>
            <div className="flex items-center gap-4">
              <button onClick={() => setView('dashboard')} className={`hover:underline ${view === 'dashboard' ? 'underline' : ''}`}>
                Dashboard
              </button>
              <button onClick={() => setView('shop')} className={`hover:underline ${view === 'shop' ? 'underline' : ''}`}>
                Shop
              </button>
              <button onClick={() => setView('orders')} className={`hover:underline ${view === 'orders' ? 'underline' : ''}`}>
                Orders
              </button>
              <button onClick={() => setView('profile')} className={`hover:underline ${view === 'profile' ? 'underline' : ''}`}>
                <User size={16} />
              </button>
              <button onClick={signOut} className="flex items-center gap-2 hover:underline">
                <LogOut size={16} />
              </button>
            </div>
          </div>
        </nav>

        <div className="max-w-7xl mx-auto p-6">
          {view === 'dashboard' && <CustomerDashboard customer={customerData} orders={customerOrders} />}
          {view === 'shop' && <CustomerShop customer={customerData} products={allProducts} onOrderPlaced={() => setView('orders')} />}
          {view === 'orders' && <CustomerOrders orders={customerOrders} loading={customerOrdersLoading} />}
          {view === 'profile' && <CustomerProfile customer={customerData} />}
        </div>
      </div>
    );
  }

  // Wholesaler View (legacy - keep for backward compatibility)
  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-black text-white p-4">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <h1 className="text-xl font-bold">4Skin Wholesale</h1>
          <div className="flex items-center gap-4">
            <button onClick={() => setView('products')} className={`hover:underline ${view === 'products' ? 'underline' : ''}`}>
              Products
            </button>
            <button onClick={() => setView('orders')} className={`hover:underline ${view === 'orders' ? 'underline' : ''}`}>
              My Orders
            </button>
            <button onClick={() => setView('profile')} className={`hover:underline ${view === 'profile' ? 'underline' : ''}`}>
              <User size={16} />
            </button>
            <button onClick={() => setView('cart')} className="flex items-center gap-1 hover:underline">
              <ShoppingCart size={18} />
              {itemCount > 0 && (
                <span className="bg-red-500 text-xs px-1.5 py-0.5 rounded-full">{itemCount}</span>
              )}
            </button>
            <button onClick={signOut} className="flex items-center gap-2 hover:underline">
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto p-6">
        {view === 'products' && (
          <ProductCatalog
            products={products}
            onAddToCart={addToCart}
            userTier={userData?.pricingTier}
            tierDiscount={userData?.tierDiscount || 0}
          />
        )}
        {view === 'cart' && (
          <Cart
            cart={cart}
            onUpdateQuantity={updateQuantity}
            onRemove={removeFromCart}
            onCheckout={() => setView('checkout')}
            subtotal={subtotal}
            ccFee={ccFee}
            totalWithFee={totalWithFee}
          />
        )}
        {view === 'checkout' && (
          <Checkout
            cart={cart}
            userData={userData}
            subtotal={subtotal}
            ccFee={ccFee}
            totalWithFee={totalWithFee}
            onSuccess={handleCheckoutSuccess}
            onCancel={() => setView('cart')}
          />
        )}
        {view === 'orders' && <OrderHistory orders={orders} loading={ordersLoading} />}
        {view === 'profile' && <Profile userData={userData} userId={user.uid} />}
      </div>
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}
